import { Injectable } from '@angular/core';
import { environment } from 'environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, lastValueFrom } from 'rxjs';
import { CustomerService } from '../customer/customer.service';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { DataSnapshot, getDatabase, limitToLast, query, ref } from 'firebase/database';
import { object } from 'rxfire/database';
import { QueryChange } from '@dashboard_models/rtdb';
import { AW } from '@airwallet/types';

@Injectable({
    providedIn: 'root'
})
export class ActionsService {
    private alertToggled$ = new BehaviorSubject<any>({});
    private adviceToggled$ = new BehaviorSubject<any>({});
    selectedAlertToggled$$ = this.alertToggled$.asObservable();
    selectedAdviceToggled$ = this.adviceToggled$.asObservable();

    subcustomerUids: Record<string, string> = {};

    private database = getDatabase();

    constructor(
        private http: HttpClient,
        private customerService: CustomerService,
        private localStorageService: LocalStorageService
    ) {}

    setAlertToggled(toggled: boolean) {
        this.alertToggled$.next(toggled);
    }
    setAdviceToggled(toggled: boolean) {
        this.adviceToggled$.next(toggled);
    }

    readActions(uid: string, limit: number, startAfterKey?: string): Observable<QueryChange> {
        let queryRef = query(ref(this.database, `actions/${uid}`), limitToLast(limit));

        if (startAfterKey) {
            queryRef = query(ref(this.database, `actions/${uid}`), limitToLast(limit));
        }

        return object(queryRef);
    }

    async getActionsFromDB(uid: string, limit = 100): Promise<any> {
        const actionRecords: Record<string, AW.Operator.Action.Action> = {};
        await this.getSubcustomerUidAndName(uid).then(async subcustomers => {
            const promiseArray: Promise<any>[] = [];

            for (const subcustomerKey in subcustomers) {
                promiseArray.push(this.getActionFromSDK(subcustomerKey, subcustomers[subcustomerKey], limit));
            }

            await Promise.all(promiseArray).then(actions => {
                actions.forEach((actionRecord: Record<string, AW.Operator.Action.Action>) => {
                    if (actionRecord) {
                        for (const actionRecordKey in actionRecord) {
                            actionRecords[actionRecordKey] = actionRecord[actionRecordKey];
                        }
                    }
                });
            });
        });
        return actionRecords;
    }

    async getActionFromSDK(subcustomerUid: string, subcustomerName: string, limit: number, startAfterKey?: string) {
        return new Promise(resolve => {
            const actionSub: Subscription = this.readActions(subcustomerUid, limit, startAfterKey).subscribe(actionQuery => {
                actionSub.unsubscribe();
                const actionRecord: Record<string, AW.Operator.Action.Action> = actionQuery.snapshot.val();
                for (const actionKey in actionRecord) {
                    actionRecord[actionKey].customer_name = subcustomerName;
                }
                return resolve(actionRecord);
            });
        });
    }

    async getSubcustomerUidAndName(uid: string): Promise<Record<string, string>> {
        return new Promise((resolve: any, reject: any) => {
            this.subcustomerUids[uid] = 'My account';
            const subcustomer_sub: Subscription = this.customerService.readSubCustomers(uid).subscribe(query => {
                const subcustomer_snap: DataSnapshot = query.snapshot;
                subcustomer_sub.unsubscribe();
                if (subcustomer_snap.val()) {
                    const subcustomers: Record<string, AW.Account.SubCustomer> = subcustomer_snap.val();
                    for (const subuid in subcustomers) {
                        this.subcustomerUids[`${subuid}_operated_by_${uid}`] = subcustomers[subuid].call_name ? subcustomers[subuid].call_name : subcustomers[subuid].name;
                    }
                }
                return resolve(this.subcustomerUids);
            });
        });
    }

    async getActionsFromBackend(filters?: { showAdvices: boolean; showAlerts: boolean; showClosed: boolean }): Promise<any> {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(
                this.http.get<Record<string, AW.Operator.Action.Action>>(`${environment.baseUrl}/api_actions/actions/fetch`, {
                    params: {
                        filters: JSON.stringify(filters ? filters : {})
                    }
                })
            )
                .then((res: Record<string, AW.Operator.Action.Action>) => {
                    // Adds customer name to action objects, as Backend does not do this.
                    for (const actionKey in res) {
                        res[actionKey].customer_name = this.subcustomerUids[res[actionKey].customer_id];
                    }
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    async checkAction(action: AW.Operator.Action.Action, logKeys: string[]): Promise<any> {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(
                this.http.put<Record<string, AW.Operator.Action.Action>>(`${environment.baseUrl}/api_actions/close`, {
                    action,
                    logKeys,
                    userName: this.localStorageService.getItem('authenticatedUser')?.name
                })
            )
                .then((res: Record<string, AW.Operator.Action.Action>) => {
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    async uncheckAction(action: AW.Operator.Action.Action, logKey: string): Promise<any> {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(
                this.http.put<Record<string, AW.Operator.Action.Action>>(`${environment.baseUrl}/api_actions/unclose`, {
                    action,
                    logKey,
                    userName: this.localStorageService.getItem('authenticatedUser')?.name
                })
            )
                .then((res: Record<string, AW.Operator.Action.Action>) => {
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    async checkMultipleActions(action: AW.Operator.Action.Action, logKeys: string[]): Promise<any> {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(
                this.http.put<Record<string, AW.Operator.Action.Action>>(`${environment.baseUrl}/api_actions/close_multiple`, {
                    action,
                    logKeys,
                    userName: this.localStorageService.getItem('authenticatedUser')?.name
                })
            )
                .then((res: Record<string, AW.Operator.Action.Action>) => {
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    convertActionsToArray(actionsObj: AW.Operator.Action.Action[]): AW.Operator.Action.Action[] {
        const actionArray = [];
        for (const actionKey in actionsObj) {
            actionArray.push(actionsObj[actionKey]);
        }
        return actionArray;
    }

    showNoActionsBox(actions: AW.Operator.Action.Action[], showClosed: boolean, loading?: boolean): boolean {
        if (loading) {
            return false;
        }
        const activeActions = [];
        for (const action of actions) {
            if (action.active_actions) {
                activeActions.push(action);
            }
        }
        if (activeActions.length === 0 && !showClosed) {
            return true;
        }
        return false;
    }
}
